import { Visibility } from "@mui/icons-material";
import InputIcon from "@mui/icons-material/Input";
import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import SearchBox from "Common/Components/SearchBox/SearchBox";
import MasterTableContainer from "Common/Tables/MasterFiltersTable/MasterTableContainer";
import LoadingSpinner from "components/LoadingSpinner";
import { app_modules } from "helpers/apps_modules_list";
import usePermission from "hooks/usePermission";
import React, { useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { createSelector } from "reselect";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import useClearStateOnNavigation from "CustomHooks/useClearStateOnNavigation";
import NameWithDate from "Common/Components/NameWithDate.tsx/NameWithDate";
import { fetchPendingChallans, fetchPendingChallanDropdown, exportPendingRACSV } from "slices/Warehouse/ItemInward/ReturnedAccessories/thunk";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import ExportButton from "Common/Components/Buttons/ExportButton";
import StatusChip from "pages/Enquiry/Components/StatusChip";

type Props = { warehouseId?: string };

const ReturnedAccessoriesTab: React.FC<Props> = ({ warehouseId }) => {
  document.title = "Returned Accessories – Item Inward";

  const dispatch = useDispatch<any>();

  const location = useLocation();

  const selector = createSelector(
    (state: any) => state.returnedAccessoriesInward,
    (s) => ({
      list: Array.isArray(s?.pendingChallans) ? s.pendingChallans : [],
      rowCount: s?.pendingChallansCount || 0,
      loading: !!s?.pendingChallansLoading,
    }),
  );
  const { list, rowCount, loading } = useSelector(selector);

  const { canView } = usePermission(
    app_modules?.warehouse?.app_name,
    app_modules?.warehouse?.modules?.item_inward,
  );

  const [tableInstance, setTableInstance] = useState<any>(null);
  const [data, setData] = useState<any[]>([]);

  const tableState = usePreservedTableState({
    key: `returnedAccessoriesTable-${warehouseId || 'all'}`,
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = tableState;

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  useEffect(() => {
    dispatch(
      fetchPendingChallans({
        warehouseId,
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: filterValue,
      })
    );
  }, [dispatch, warehouseId, pageIndex, pageSize, sortField, sortDirection, search, filterValue]);

  useEffect(() => {
    setData(list);
  }, [list]);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 500);
    return () => clearTimeout(t);
  }, [searchInput]);

  useClearStateOnNavigation(location, resetState);

  const fetchDropdown = async (field: string) => {
    const result = await dispatch(fetchPendingChallanDropdown({ field, warehouseId }));
    return result.payload?.data || [];
  };

  const columns = useMemo(
    () => [

      {
        header: "Challan No.",
        accessorKey: "challan_no",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT.WAREHOUSE?.ITEM_INWARD?.CHALLAN_ACCESSORY_INWARD?.FILTER_DROPDOWN('challan_no', warehouseId)}`,
        meta: { filterType: "dropdown", fetchDropdownValues: () => fetchDropdown("challan_no") },
        cell: ({ row }: any) => row.original.challan_no || "—",
      },
      {
        header: "Challan Date",
        accessorKey: "challan_date",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "date" as const,
        cell: ({ row }: any) =>
          row.original.challan_date ? (
            <FormattedDate date={row.original.challan_date} />
          ) : (
            "—"
          ),
      },
      {
        header: "SO Number",
        accessorKey: "sales_order_number",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT.WAREHOUSE?.ITEM_INWARD?.CHALLAN_ACCESSORY_INWARD?.FILTER_DROPDOWN('sales_order_number', warehouseId)}`,
        meta: { filterType: "dropdown", fetchDropdownValues: () => fetchDropdown("sales_order_number") },
        cell: ({ row }: any) => row.original.sales_order_number || "—",
      },
       {
        header: "Status",
        accessorKey: "status",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT.WAREHOUSE?.ITEM_INWARD?.CHALLAN_ACCESSORY_INWARD?.FILTER_DROPDOWN('status',warehouseId)}`,
        meta: { filterType: "dropdown", fetchDropdownValues: () => fetchDropdown("status") },
        cell: ({ row }: any) => <StatusChip status={row.original.status} />,
      },
      {
        header: "Warehouse",
        accessorKey: "warehouse_name",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT.WAREHOUSE?.ITEM_INWARD?.CHALLAN_ACCESSORY_INWARD?.FILTER_DROPDOWN('warehouse_name', warehouseId)}`,
        meta: { filterType: "dropdown", fetchDropdownValues: () => fetchDropdown("warehouse_name") },
        cell: ({ row }: any) => row.original.warehouse_name || "—",
      },
      {
        header: "Created By",
        accessorKey: "created_at",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "date" as const,
        meta: { filterType: "dropdown", fetchDropdownValues: () => fetchDropdown("created_by_user__first_name") },
        cell: ({ row }: any) =>
          row.original.created_by ? (
            <NameWithDate
              name={row.original.created_by}
              date={row.original.created_at}
            />
          ) : (
            "—"
          ),
      },
      {
        header: "Updated By",
        accessorKey: "updated_at",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "date" as const,
        meta: { filterType: "dropdown", fetchDropdownValues: () => fetchDropdown("updated_by_user__first_name") },
        cell: ({ row }: any) =>
          row.original.updated_by ? (
            <NameWithDate
              name={row.original.updated_by}
              date={row.original.updated_at}
            />
          ) : (
            "—"
          ),
      },
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: any) => {
          const d = row.original;
          const menuItems = [
            {
              icon: Visibility,
              label: "View",
              permission: canView,
              link: () => `/warehouse/item-inward/returned-accessories/${d.challan_id}/view`,
              color: "primary",
            },
            {
              icon: InputIcon,
              label: "Inward",
              permission: canView, // Should probably be a 'canCreate' permission, using canView for now
              link: () => `/warehouse/item-inward/returned-accessories/${d.challan_id}/process?return_id=${d.return_id}`,
              color: "primary",
            },
          ].filter((item) => item.permission);
          return <DynamicMenuItem row={d} menuItems={menuItems} />;
        },
      },
    ],
    [canView],
  );

  return (
    <div
      className="d-flex flex-column"
      style={{ flex: "1 1 auto", minHeight: 0, maxHeight: "100%", overflow: "hidden" }}
    >
      <div className="flex-shrink-0">
        <Row className="align-items-center mecord-search-row g-2">
          <SearchBox
            value={searchInput}
            onChange={(e: any) => setSearchInput(e.target.value)}
            placeholder="Search challan no., SO number, warehouse..."
          />
          <Col xs={12} md={12} lg={8} xl={7} className="d-flex justify-content-end align-items-center gap-2">
            {tableInstance && (
              <ColumnVisibilityDropdown
                columns={tableInstance.getAllLeafColumns()}
                excludedColumns={["action"]}
              />
            )}
            {canView && (
              <ExportButton
                onExport={async () => {
                  return await exportPendingRACSV({
                    warehouse_id: warehouseId || "",
                    search,
                    filters: filterValue,
                    sort: sorting[0]?.id,
                    order: sorting[0]?.desc ? "desc" : "asc",
                  });
                }}
              />
            )}
          </Col>
        </Row>
      </div>

      <div
        style={{
          flex: "1 1 auto",
          minHeight: 0,
          maxHeight: "100%",
          marginTop: 4,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading ? (
          <LoadingSpinner />
        ) : (
          <MasterTableContainer
            columns={columns}
            data={data}
            totalRecords={rowCount}
            pageIndex={pageIndex}
            pageSize={pageSize}
            onPageChange={setPageIndex}
            onPageSizeChange={setPageSize}
            columnFilters={columnFilters}
            setColumnFilters={setColumnFilters}
            colValue={setFilterValue}
            sorting={sorting}
            onSortingChange={setSorting}
            isBordered={false}
            isPagination={true}
            manualSorting={true}
            onTableReady={setTableInstance}
          />
        )}
      </div>
    </div>
  );
};

export default ReturnedAccessoriesTab;
